import type { AssetFileFormData } from 'modules/assets/files/components/AssetFileImportFormModal';
import type { IssueData } from 'modules/assets/issues/components/ReportIssueButton/IssueData.type';
import type { FiltersGQL } from 'modules/filters/model';
import type { WorkOrderData } from 'modules/maintenance/workOrders/components/CreateWorkOrderButton/WorkOrderData.type';
import type {
  WorkOrderCriticality,
  WorkOrderStatus,
} from 'modules/maintenance/workOrders/models/WorkOrder.entity';
import type { ContractAssignmentImportTemplateFormat } from 'modules/out-of-contract/components/ContractAssignmentImportTab/types';
import type { FetchApi } from '../client';

export type RequestExportInventoryParams = {
  filters: FiltersGQL;
  search: string;
  columns: string[];
};

export type RequestExportArchivedInventoryParams = {
  search: string;
  columns: string[];
};

type RequestExportPendingInvitationsParams = {
  search: string;
};

type RequestExportOrganizationUsersParams = {
  search: string;
};

type RequestExportOutOfContractUsageParams = {
  fileType: 'XLSX';
  since: string;
  until: string;
  assetFilters?: FiltersGQL;
  filters?: {
    status?: string[];
    type?: string[];
    contractReference?: string[];
    contractStatus?: string[];
    customer?: string[];
    owner?: string[];
  };
  search?: string;
  columns?: string[];
};

type InventoryMassUpdateTaskAccepted = {
  id: number;
  status: string;
};

export const fleetApi = (fetchApi: FetchApi) => ({
  downloadAssetInOrganizationFile(
    token: string,
    assetInOrganizationFileId: number,
  ): Promise<Response> {
    const endpoint = fetchApi.generateEndpoint(
      `/app/assetInOrganizationFiles/${assetInOrganizationFileId}/download`,
    );

    return fetch(endpoint, {
      method: 'GET',
      headers: {
        'x-access-token': token,
      },
    });
  },

  downloadWorkOrderAttachment(
    token: string,
    workOrderId: number,
    workOrderAttachmentId: number,
  ): Promise<Response> {
    const endpoint = fetchApi.generateEndpoint(
      `/app/workOrder/${workOrderId}/attachments/${workOrderAttachmentId}/download`,
    );

    return fetch(endpoint, {
      method: 'GET',
      headers: {
        'x-access-token': token,
      },
    });
  },

  createWorkOrder(
    token: string,
    assetId: number,
    formData: WorkOrderData,
  ): Promise<void> {
    const {
      files,
      name,
      description,
      criticality,
      startDate,
      endDate,
      performedBy,
      location,
      tasks,
      assignedTo,
      note,
      plannedDate,
      status,
      maintenanceServiceId,
      issueId,
      durationInMinutes,
      type,
      cumulativeKilometersAtCompletion,
      cumulativeHoursAtCompletion,
    } = formData;

    const isDone = status === 'done';

    const data: {
      assetId: number;
      name: string;
      description: string;
      criticality: WorkOrderCriticality;
      startDate?: string;
      endDate?: string;
      performedBy: string;
      location: string;
      assignedToUserId?: number;
      note: string;
      plannedDate?: string;
      status?: WorkOrderStatus;
      maintenanceServiceId?: number;
      issueId?: number;
      durationInMinutes?: number;
      type: WorkOrderData['type'];
      cumulativeKilometersAtCompletion?: number;
      cumulativeHoursAtCompletion?: number;
    } = {
      assetId,
      name,
      description,
      criticality,
      performedBy,
      location,
      note,
      type,
      ...(isDone ? {} : { status }),
      ...(assignedTo && { assignedToUserId: assignedTo.id }),
      ...(plannedDate && { plannedDate }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(maintenanceServiceId && { maintenanceServiceId }),
      ...(issueId && { issueId }),
      ...(durationInMinutes !== undefined && { durationInMinutes }),
      ...(cumulativeKilometersAtCompletion !== undefined && {
        cumulativeKilometersAtCompletion,
      }),
      ...(cumulativeHoursAtCompletion !== undefined && {
        cumulativeHoursAtCompletion,
      }),
    };

    const taskFields = tasks.reduce<Record<string, string>>(
      (fields, task, index) => {
        fields[`tasks[${index}][content]`] = task.content;
        if (task.maintenanceServiceConfigurationTaskId !== undefined) {
          fields[`tasks[${index}][maintenanceServiceConfigurationTaskId]`] =
            task.maintenanceServiceConfigurationTaskId.toString();
        }
        return fields;
      },
      {},
    );

    return fetchApi
      .setToken(token)
      .postFiles(
        isDone ? '/app/workOrder/done' : '/app/workOrder',
        { ...data, ...taskFields },
        files,
      );
  },

  createWorkOrderFromDocument(
    token: string,
    assetId: number,
    file: File,
    documentKind: string,
  ): Promise<{ id: number }> {
    const data = {
      assetId: assetId.toString(),
      documentKind,
    };

    return fetchApi
      .setToken(token)
      .postFile('/app/workOrder/from-document', data, file);
  },

  downloadIssueAttachment(
    token: string,
    issueId: number,
    issueAttachmentId: number,
  ): Promise<Response> {
    const endpoint = fetchApi.generateEndpoint(
      `/app/assetInOrganizationIssue/${issueId}/attachments/${issueAttachmentId}/download`,
    );

    return fetch(endpoint, {
      method: 'GET',
      headers: {
        'x-access-token': token,
      },
    });
  },
  createIssue(
    token: string,
    assetId: number,
    formData: IssueData,
  ): Promise<void> {
    const {
      files,
      title,
      description,
      isCritical,
      assignee,
      status,
      location,
    } = formData;

    const data: {
      assetId: number;
      title: string;
      description: string;
      location: string;
      isCritical: string;
      assigneeUserId?: number;
      status: string;
    } = {
      assetId,
      title,
      description,
      location,
      isCritical: isCritical ? 'true' : 'false',
      status,
    };

    if (assignee) {
      data.assigneeUserId = assignee.id;
    }

    return fetchApi
      .setToken(token)
      .postFiles('/app/assetInOrganizationIssue', data, files);
  },
  addIssueAttachment(
    token: string,
    issueId: number,
    files: File[],
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .postFiles(
        `/app/assetInOrganizationIssue/${issueId}/attachments`,
        {},
        files,
      );
  },
  uploadAssetInOrganizationFile(
    token: string,
    assetId: number,
    formData: AssetFileFormData,
  ): Promise<void> {
    const { file, shared, isPublic, name, expirationDate } = formData;
    const data: {
      assetId: string;
      shared: string;
      isPublic: string;
      name: string;
      expirationDate?: string;
    } = {
      assetId: assetId.toString(),
      shared: shared.toString(),
      isPublic: isPublic.toString(),
      name,
    };

    if (expirationDate) {
      data.expirationDate = expirationDate;
    }

    return fetchApi
      .setToken(token)
      .postFile('/app/assetInOrganizationFiles/upload', data, file);
  },
  uploadAssetInOrganizationPicture(
    token: string,
    assetId: number,
    file: File,
    setAsPrimary = true,
  ): Promise<void> {
    return fetchApi.setToken(token).postFile(
      '/app/assetInOrganizationPictures/upload',
      {
        assetId: assetId.toString(),
        setAsPrimary: setAsPrimary.toString(),
      },
      file,
    );
  },
  requestInventoryMassUpdate(
    token: string,
    file: File,
  ): Promise<InventoryMassUpdateTaskAccepted> {
    return fetchApi
      .setToken(token)
      .postFile('/app/inventory/massUpdate', {}, file);
  },
  requestExportInventory(
    token: string,
    params: RequestExportInventoryParams,
    language: string,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/inventory/distributedExport', {
        fileType: 'XLSX',
        assetFilters: params.filters,
        search: params.search,
        columns: params.columns,
        language,
      })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occured while requesting your export. Please try again later',
        );
      });
  },
  requestExportArchivedInventory(
    token: string,
    params: RequestExportArchivedInventoryParams,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/inventory/exportV2/archived', { ...params, filters: {} })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occured while requesting your export. Please try again later',
        );
      });
  },
  requestExportPendingInvitations(
    token: string,
    params: RequestExportPendingInvitationsParams,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/organization/pendingInvitations/export', params)
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occured while requesting your export. Please try again later',
        );
      });
  },
  requestExportOrganizationUsers(
    token: string,
    params: RequestExportOrganizationUsersParams,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/organization/users/export', params)
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occured while requesting your export. Please try again later',
        );
      });
  },
  addWorkOrderAttachment(
    token: string,
    workOrderId: number,
    files: File[],
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .postFiles(`/app/workOrder/${workOrderId}/attachments`, {}, files);
  },

  downloadOutOfContractUsageAttachment(
    token: string,
    outOfContractUsageId: number,
    attachmentId: number,
  ): Promise<Response> {
    const endpoint = fetchApi.generateEndpoint(
      `/app/outOfContractUsage/${outOfContractUsageId}/attachments/${attachmentId}/download`,
    );

    return fetch(endpoint, {
      method: 'GET',
      headers: {
        'x-access-token': token,
      },
    });
  },

  requestExportOutOfContractUsage(
    token: string,
    params: RequestExportOutOfContractUsageParams,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/outOfContractUsage/export', params);
  },

  addOutOfContractUsageAttachment(
    token: string,
    outOfContractUsageId: number,
    files: File[],
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .postFiles(
        `/app/outOfContractUsage/${outOfContractUsageId}/attachments`,
        {},
        files,
      );
  },
  downloadContractAssignmentAttachment(
    token: string,
    contractAssignmentId: number,
    attachmentId: number,
  ): Promise<Response> {
    const endpoint = fetchApi.generateEndpoint(
      `/app/contractAssignments/${contractAssignmentId}/attachments/${attachmentId}/download`,
    );

    return fetch(endpoint, {
      method: 'GET',
      headers: {
        'x-access-token': token,
      },
    });
  },

  addContractAssignmentAttachment(
    token: string,
    contractAssignmentId: number,
    files: File[],
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .postFiles(
        `/app/contractAssignments/${contractAssignmentId}/attachments`,
        {},
        files,
      );
  },
  /**
   * Resolves with `{ createdCount, updatedCount }`. A file the API refused row by
   * row rejects with `{ errors, errorCount }`, hence the unknown shapes.
   */
  importContractAssignments(token: string, file: File): Promise<unknown> {
    return fetchApi
      .setToken(token)
      .postFile('/app/contractAssignments/import', {}, file);
  },

  downloadContractAssignmentImportTemplate(
    token: string,
    format: ContractAssignmentImportTemplateFormat = 'csv',
  ): Promise<Response> {
    const endpoint = fetchApi.generateEndpoint(
      `/app/contractAssignments/import/template?format=${format}`,
    );

    return fetch(endpoint, {
      method: 'GET',
      headers: {
        'x-access-token': token,
      },
    });
  },
});
